import { ADMIN_ROLE, MANAGER_ROLE } from '@/lib/user/roles';
import type { RoleType } from '@/lib/user/roles';
import {
    INSTAGRAM,
    FACEBOOK,
    LINKEDIN,
    PINTEREST,
    TWITTER,
    TIKTOK_BUSINESS,
    YOUTUBE
} from '@/lib/socialProfile';
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

import {
    // Dashboard
    DASHBOARD_GENERATE_CUSTOM_REPORT,
    DASHBOARD_CUSTOMIZATION,
    // Analytics
    ANALYZE_STORIES,
    ANALYZE_PROFILE_ACTIVITY,
    ANALYZE_VIDEOS,
    ANALYZE_PERFORMANCE,
    ANALYZE_REACH,
    ANALYZE_REACH_IMPRESSIONS,
    ANALYZE_CONTENT,
    ANALYZE_IMPRESSIONS,
    ANALYZE_INDUSTRY_BENCHMARK,
    ANALYZE_COMMUNITY,
    ANALYZE_MENTIONS,
    ANALYZE_REELS,
    ANALYZE_ENGAGEMENT,
    // Publishing
    SCHEDULER,
    CAPTIONS_LISTS,
    SCHEDULER_FEED_VIEW,
    MEDIA_LIBRARY,
    SCHEDULER_COLLABORATION,
    SCHEDULER_COLLABORATION_SETTINGS,
    SCHEDULER_COLLABORATION_NOTIFICATIONS,
    // Conversation
    MANAGE_DIRECT_MESSAGES,
    MANAGE_MENTIONS_TRACKER,
    MANAGE_COMMENTS_TRACKER,
    // Listening
    LISTENING_FEED,
    LISTENING_FEEDS,
    LISTENING_HASHTAG,
    LISTENING_HASHTAGS,
    LISTENING_COMPETITOR,
    LISTENING_COMPETITORS,
    // Settings
    USERS_ROLES,
    SUBSCRIPTION_INVOICES,
    INTEGRATIONS,
    // Reports
    REPORTS,
    REPORTS_ACCOUNTS,
    REPORTS_COMPETITORS,
    REPORTS_HASHTAGS,
    REPORTS_EDIT,
    REPORTS_DASHBOARDS,
    // Content
    CONTENT_POSTS,
    CONTENT_ALBUMS,
    CONTENT_ALBUM_POSTS,
    CONTENT_ALBUM_ANALYTICS,
    CONTENT_ALBUMS_COMPARATIVE_TABLE,
    MEDIA,
    // Discover
    DISCOVER
} from '@/lib/routes/privateRoutes';

import {
    DASHBOARD_REPORT_CUSTOMIZATION,
    COLLABORATION as COLLABORATION_RIGHT,
    MENTIONS,
    TREE_STRUCTURE
} from '@/lib/acl/aclRightsList';

type PageRightsType = Record<
    string,
    {
        permissions?: string[];
        networks?: SocialProfileNetworkType[];
        roles?: RoleType[];
        acls?: Partial<Record<SocialProfileNetworkType, string[]>>;
        group?: boolean;
    }
>;

const pageRights: PageRightsType = {
    // Dashboard
    [DASHBOARD_GENERATE_CUSTOM_REPORT]: {
        permissions: [DASHBOARD_REPORT_CUSTOMIZATION.read]
    },
    [DASHBOARD_CUSTOMIZATION]: {
        permissions: [DASHBOARD_REPORT_CUSTOMIZATION.read]
    },
    // Analytics
    [ANALYZE_ENGAGEMENT]: { group: false },
    [ANALYZE_STORIES]: {
        networks: [INSTAGRAM],
        group: false
    },
    [ANALYZE_PROFILE_ACTIVITY]: {
        networks: [INSTAGRAM],
        group: false
    },
    [ANALYZE_VIDEOS]: {
        networks: [FACEBOOK],
        group: false
    },
    [ANALYZE_PERFORMANCE]: {
        networks: [FACEBOOK],
        group: false
    },
    [ANALYZE_REACH]: {
        networks: [INSTAGRAM, FACEBOOK, LINKEDIN],
        group: false
    },
    [ANALYZE_REACH_IMPRESSIONS]: {
        networks: [INSTAGRAM, FACEBOOK, LINKEDIN, TWITTER],
        group: false,
        permissions: [TREE_STRUCTURE.read]
    },
    [ANALYZE_CONTENT]: {
        networks: [FACEBOOK, TWITTER, LINKEDIN, PINTEREST, TIKTOK_BUSINESS, YOUTUBE],
        group: false
    },
    [ANALYZE_IMPRESSIONS]: {
        networks: [TWITTER],
        group: false
    },
    [ANALYZE_INDUSTRY_BENCHMARK]: {
        networks: [FACEBOOK, INSTAGRAM],
        group: false
    },
    [ANALYZE_COMMUNITY]: {
        networks: [FACEBOOK, INSTAGRAM, LINKEDIN, PINTEREST, TWITTER, TIKTOK_BUSINESS, YOUTUBE],
        group: false
    },
    [ANALYZE_MENTIONS]: {
        networks: [INSTAGRAM],
        permissions: [MENTIONS.read],
        group: false
    },
    [ANALYZE_REELS]: {
        networks: [INSTAGRAM, FACEBOOK],
        group: false
    },
    [LISTENING_COMPETITOR]: {
        networks: [INSTAGRAM, FACEBOOK],
        group: false
    },
    [LISTENING_COMPETITORS]: {
        networks: [INSTAGRAM, FACEBOOK],
        group: false
    },
    // media library
    [MEDIA_LIBRARY]: {
        networks: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
        group: false
    },
    // settings
    [USERS_ROLES]: {
        roles: [ADMIN_ROLE, MANAGER_ROLE]
    },
    [SUBSCRIPTION_INVOICES]: {
        roles: [ADMIN_ROLE] // remove access to subscription invoices for non-admins users
    },
    [INTEGRATIONS]: {
        roles: [ADMIN_ROLE] // remove access to subscription invoices for non-admins users
    },
    [MANAGE_DIRECT_MESSAGES]: {
        networks: [INSTAGRAM],
        group: false
    },
    // Publishing
    [CAPTIONS_LISTS]: {
        networks: [FACEBOOK, INSTAGRAM, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
        group: false
    },
    [SCHEDULER]: {
        networks: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
        group: true
    },
    [SCHEDULER_FEED_VIEW]: {
        networks: [INSTAGRAM],
        group: false
    },
    [SCHEDULER_COLLABORATION]: { group: true },
    [SCHEDULER_COLLABORATION_SETTINGS]: {
        networks: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
        permissions: [COLLABORATION_RIGHT.read],
        group: true
    },
    [SCHEDULER_COLLABORATION_NOTIFICATIONS]: {
        networks: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
        permissions: [COLLABORATION_RIGHT.read],
        group: true
    },
    // Listening
    [LISTENING_HASHTAG]: {
        networks: [INSTAGRAM],
        group: false
    },
    [LISTENING_HASHTAGS]: {
        networks: [INSTAGRAM],
        group: false
    },
    [LISTENING_FEED]: {
        networks: [INSTAGRAM, FACEBOOK],
        group: false
    },
    [LISTENING_FEEDS]: {
        networks: [INSTAGRAM, FACEBOOK],
        group: false
    },
    // Manage
    [MANAGE_MENTIONS_TRACKER]: {
        networks: [INSTAGRAM],
        permissions: [MENTIONS.read],
        group: false
    },
    [MANAGE_COMMENTS_TRACKER]: {
        networks: [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS, LINKEDIN],
        group: false
    },
    // Reports
    [REPORTS]: { group: false },
    [REPORTS_ACCOUNTS]: { group: false },
    [REPORTS_COMPETITORS]: { group: false },
    [REPORTS_HASHTAGS]: { group: false },
    [REPORTS_EDIT]: { group: false },
    [REPORTS_DASHBOARDS]: {
        group: false
    },
    // Content
    [CONTENT_POSTS]: { group: false },
    [CONTENT_ALBUMS]: { group: false },
    [CONTENT_ALBUM_POSTS]: { group: false },
    [CONTENT_ALBUM_ANALYTICS]: { group: false },
    [CONTENT_ALBUMS_COMPARATIVE_TABLE]: { group: false },
    [MEDIA]: { group: false },
    // Discover
    [DISCOVER]: { group: false }
};

export default pageRights;
